/**
 * The operator's handbook.
 *
 * Written to be read once, end to end, by whoever takes over the product —
 * and consulted afterwards. It follows the money and the decisions rather
 * than the screens, because the screens are easy to click and hard to
 * understand: what matters is knowing which clicks move money, which are
 * irreversible, and what the platform has already promised the user in the
 * terms.
 *
 * Everything here describes what the code actually does. When that changes,
 * this page has to change with it.
 */

function Section({
  n,
  title,
  children,
}: {
  n: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section style={{ marginBottom: 40 }}>
      <h2
        style={{
          fontSize: 15,
          margin: '0 0 12px',
          display: 'flex',
          alignItems: 'baseline',
          gap: 10,
        }}
      >
        <span style={{ color: 'var(--accent)', fontVariantNumeric: 'tabular-nums' }}>
          {n}
        </span>
        {title}
      </h2>
      <div
        style={{
          fontSize: 13,
          lineHeight: 1.7,
          color: 'var(--text)',
          // The panel collapses paragraph margins; a grid gap spaces the
          // blocks evenly without fighting it. This page is meant to be read,
          // not scanned.
          display: 'grid',
          gap: 10,
        }}
      >
        {children}
      </div>
    </section>
  );
}

function Warning({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        borderLeft: '3px solid var(--danger)',
        background: 'rgba(255,107,107,0.06)',
        padding: '10px 14px',
        borderRadius: 4,
        margin: '12px 0',
      }}
    >
      {children}
    </div>
  );
}

function Note({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        borderLeft: '3px solid var(--accent)',
        background: 'rgba(0,162,244,0.06)',
        padding: '10px 14px',
        borderRadius: 4,
        margin: '12px 0',
      }}
    >
      {children}
    </div>
  );
}

export default function Guide() {
  return (
    <div style={{ maxWidth: 760 }}>
      <div className="section-head">
        <div>
          <div className="section-title">
            Comment l’argent circule, ce que vous décidez, et ce qui est
            irréversible
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>
            À lire une fois en entier, puis à consulter. Tout ce qui suit
            décrit le comportement réel du produit.
          </div>
        </div>
      </div>

      <Section n="1." title="Ce que Staynow est, du point de vue de l’argent">
        <p>
          Staynow n’héberge personne. La plateforme met en relation un voyageur
          et le bénéficiaire d’une offre, encaisse le voyageur, et verse au
          bénéficiaire moins la commission.
        </p>
        <p>Il existe deux types d’offres, et la différence gouverne presque tout&nbsp;:</p>
        <ul>
          <li>
            <strong>Offre d’hôte</strong> — un hôte que vous avez validé publie
            son propre hébergement.
          </li>
          <li>
            <strong>Cession</strong> — un voyageur revend une réservation qu’il
            ne peut plus utiliser. Il n’est propriétaire de rien&nbsp;: il
            détient une réservation, et il doit le prouver.
          </li>
        </ul>
        <Note>
          Un cédant n’est pas un hôte. Il n’a pas été vetté par vous, sa preuve
          peut être fausse, et c’est pour ça que son argent ne part qu’à
          l’arrivée du voyageur. Toute la logique de risque découle de cette
          distinction.
        </Note>
      </Section>

      <Section n="2." title="Le trajet de l’argent, étape par étape">
        <p>
          <strong>À la réservation</strong> — la carte du voyageur est débitée{' '}
          <em>en entier</em>, immédiatement, par Stripe. Il n’y a pas
          d’autorisation différée&nbsp;: l’argent est pris. Il arrive dans le
          solde plateforme Stripe de Staynow.
        </p>
        <p>
          <strong>Le versement au bénéficiaire</strong> part de ce solde, par
          un virement Stripe distinct. Son moment dépend de l’offre&nbsp;:
        </p>
        <ul>
          <li>
            <strong>Hôte validé, offre d’hôte</strong> → versé{' '}
            <strong>dès la réservation</strong>. Il n’attend rien.
          </li>
          <li>
            <strong>Tout le reste</strong> (cessions, hôte non validé) → versé{' '}
            <strong>à la confirmation d’arrivée</strong> du voyageur.
          </li>
        </ul>
        <p>
          <strong>Si le voyageur ne se présente pas</strong> — un balayage
          tourne chaque minute. Une fois la fenêtre d’arrivée fermée, il verse
          quand même au bénéficiaire. Une non-présentation n’est pas remboursée,
          parce que l’offre a été retirée du fil dès la réservation et que le
          bénéficiaire a immobilisé le logement.
        </p>
        <p>
          <strong>Sur 100&nbsp;$</strong>&nbsp;: Staynow garde 14,00&nbsp;$,
          Stripe prend 3,20&nbsp;$, le bénéficiaire reçoit 82,80&nbsp;$. Les
          frais Stripe sont estimés avec une formule fixe (2,9&nbsp;%
          +&nbsp;30&nbsp;¢) et non relevés du vrai relevé&nbsp;: c’est ce qui
          permet d’annoncer un net exact à l’hôte <em>avant</em> qu’un voyageur
          réserve. Staynow absorbe l’écart.
        </p>
        <Warning>
          <strong>Staynow ne détient pas cet argent dans un compte bancaire.</strong>{' '}
          Il transite par l’infrastructure Stripe. C’est la formulation exacte
          des CGU, et il ne faut pas la simplifier en «&nbsp;Staynow retient le
          montant&nbsp;»&nbsp;: ce serait faux, et la question reviendrait lors
          d’un litige.
        </Warning>
      </Section>

      <Section n="3." title="La confirmation d’arrivée, et pourquoi elle compte">
        <p>
          Quand le versement dépend de l’arrivée, le voyageur doit confirmer sa
          présence depuis l’app. Le serveur vérifie trois choses&nbsp;: qu’il
          est dans la fenêtre horaire, que la précision GPS est suffisante, et
          qu’il est à moins de 300&nbsp;m du logement.
        </p>
        <p>
          Cette confirmation est aussi ce qui <strong>débloque le code
          d’accès</strong> pour une cession. C’est un échange&nbsp;: le voyageur
          prouve qu’il est là, il reçoit les instructions, le cédant reçoit son
          argent.
        </p>
        <Note>
          Pour l’offre d’un <strong>hôte validé</strong>, il n’y a plus de
          geste à faire&nbsp;: l’hôte est déjà payé et le code est donné
          directement. Faire glisser un doigt n’aurait rien protégé.
        </Note>
      </Section>

      <Section n="4." title="Valider un hôte">
        <p>
          Un utilisateur demande le rôle Hôte depuis l’app et remplit une courte
          candidature&nbsp;: type d’hébergement, et un lien vers son annonce
          existante s’il en a une. Vous recevez un courriel à chaque demande.
        </p>
        <p>Avant de valider, ce que vous cherchez à établir&nbsp;:</p>
        <ul>
          <li>l’hébergement existe et lui appartient, ou il a le droit de le louer&nbsp;;</li>
          <li>le lien fourni correspond bien à la personne et au lieu&nbsp;;</li>
          <li>rien ne suggère une revente déguisée d’une réservation.</li>
        </ul>
        <Warning>
          Valider un hôte a une conséquence financière directe&nbsp;: à partir
          de là, <strong>ses offres sont payées dès la réservation</strong>,
          sans attendre que le voyageur arrive. Vous transférez le risque de
          Staynow vers votre jugement. Un hôte douteux validé, c’est de l’argent
          parti avant que quiconque ait vu le logement.
        </Warning>
        <p>
          Le lien qu’il fournit n’est jamais cliquable dans le courriel — il est
          affiché en texte brut, volontairement, pour qu’un lien malveillant ne
          s’ouvre pas d’un réflexe.
        </p>
      </Section>

      <Section n="5." title="Approuver une cession">
        <p>
          Un cédant doit téléverser une preuve de sa réservation d’origine et
          déclarer le prix qu’il a réellement payé. Le prix demandé ne peut pas
          dépasser ce prix d’origine — le serveur le refuse.
        </p>
        <p>Sur la preuve, vérifiez&nbsp;:</p>
        <ul>
          <li>que le nom sur la réservation correspond au cédant&nbsp;;</li>
          <li>que les dates correspondent à l’offre publiée&nbsp;;</li>
          <li>que le montant correspond au prix d’origine déclaré&nbsp;;</li>
          <li>que le document n’a pas l’air retouché.</li>
        </ul>
        <p>
          Le prix d’origine n’est pas décoratif&nbsp;: c’est le montant barré
          que le voyageur voit sur la carte. Un prix gonflé, c’est un faux
          rabais affiché.
        </p>
      </Section>

      <Section n="6." title="Rembourser">
        <p>
          Les CGU promettent un remboursement dans quatre cas. Trois d’entre eux
          demandent votre jugement&nbsp;:
        </p>
        <ul>
          <li>
            <strong>Le paiement échoue</strong> — automatique, rien à faire.
          </li>
          <li>
            <strong>Le bénéficiaire ne fournit pas les instructions
            d’accès</strong> — désormais automatique aussi&nbsp;: le balayage
            rembourse le voyageur au lieu de payer ce bénéficiaire.
          </li>
          <li>
            <strong>Le bénéficiaire retire son offre</strong> — à faire à la
            main.
          </li>
          <li>
            <strong>Le logement n’existe pas, est inaccessible, ou ne
            correspond manifestement pas</strong> — à faire à la main, après
            avoir entendu les deux parties.
          </li>
        </ul>
        <p>
          Utilisez l’onglet <strong>Remboursements</strong>. Il reprend d’abord
          le versement déjà fait au bénéficiaire, puis rend l’argent au
          voyageur. Les deux sont liés&nbsp;: si la reprise échoue, rien n’est
          remboursé — sinon Staynow paierait le bénéficiaire de sa poche.
        </p>
        <Warning>
          Un remboursement est <strong>irréversible</strong> et ne peut être
          fait qu’une fois par réservation. Le motif que vous saisissez est la
          seule trace de la décision&nbsp;: écrivez-le pour quelqu’un qui lira
          le journal dans six mois.
        </Warning>
        <p>
          Un remboursement fait directement dans le tableau de bord Stripe est
          désormais <em>enregistré</em> côté Staynow — mais il ne reprend pas le
          versement de l’hôte. Préférez cet onglet.
        </p>
      </Section>

      <Section n="7." title="Litiges bancaires">
        <p>
          Quand un porteur de carte conteste un débit auprès de sa banque,
          Stripe débite le solde plateforme du montant contesté, que vous
          fassiez quelque chose ou non. Staynow reprend alors automatiquement
          la part correspondante au bénéficiaire déjà payé, au prorata du
          montant contesté.
        </p>
        <p>
          La réservation du voyageur n’est pas touchée et il n’est pas notifié —
          un litige est une affaire entre Staynow et Stripe. La réponse au
          litige, elle, se fait dans Stripe.
        </p>
      </Section>

      <Section n="8." title="Suppression de compte et conservation des données">
        <p>
          Un utilisateur peut supprimer son compte depuis l’app. Ce qui se passe
          réellement&nbsp;:
        </p>
        <ul>
          <li>
            son profil est <strong>anonymisé</strong>, pas effacé&nbsp;: la
            ligne survit, vidée de ses données identifiantes&nbsp;;
          </li>
          <li>
            ses <strong>réservations sont conservées</strong> — ce sont des
            pièces comptables, la loi impose de les garder&nbsp;;
          </li>
          <li>
            la suppression est <strong>refusée</strong> tant qu’une réservation
            confirmée attend une arrivée, pour qu’un versement en cours ne reste
            pas bloqué.
          </li>
        </ul>
        <p>Trois nettoyages tournent automatiquement&nbsp;:</p>
        <ul>
          <li>
            les <strong>instructions d’accès</strong> sont effacées dès la fin
            du séjour&nbsp;;
          </li>
          <li>
            les <strong>offres expirées</strong> sont anonymisées après 30
            jours, photos supprimées&nbsp;;
          </li>
          <li>
            la <strong>position GPS enregistrée à l’arrivée</strong> est
            supprimée après 30 jours — on ne garde que la distance mesurée et
            l’horodatage, soit la preuve que le contrôle a eu lieu.
          </li>
        </ul>
        <Note>
          Ces durées sont écrites dans la politique de confidentialité
          publiée. Si vous changez le code, changez le texte — et l’inverse.
          C’est le genre d’écart qu’un régulateur relève en premier.
        </Note>
      </Section>

      <Section n="9." title="Le prix baisse tout seul">
        <p>
          L’hôte fixe un prix de référence. Le serveur le réduit par paliers à
          mesure que l’échéance approche&nbsp;: −5&nbsp;% à 48&nbsp;h,
          −10&nbsp;% à 24&nbsp;h, −15&nbsp;% à 12&nbsp;h, −20&nbsp;% à
          6&nbsp;h, −30&nbsp;% à 2&nbsp;h, −35&nbsp;% à 1&nbsp;h. Le dernier
          palier affiche «&nbsp;dernière chance&nbsp;» au voyageur.
        </p>
        <p>
          Ces paliers sont en base de données, pas dans le code&nbsp;: ils se
          changent sans redéploiement. Le prix qui s’applique est celui affiché
          au moment où le voyageur confirme.
        </p>
      </Section>

      <Section n="10." title="Suspendre le signal quand quelque chose cloche">
        <p>
          Tout le produit repose sur une seule promesse&nbsp;: ce que le
          voyageur voit est réel, disponible maintenant, et disparaîtra
          vraiment quand le compte à rebours atteint zéro. Cette promesse est
          tenue ou elle est morte&nbsp;; elle ne se dégrade pas gentiment.
        </p>
        <p>
          La page <strong>Intégrité du signal</strong> porte deux
          interrupteurs. <strong>Suspendre la publication</strong> empêche les
          hôtes de mettre de nouvelles offres en ligne&nbsp;; leurs brouillons
          sont conservés. <strong>Suspendre le feed</strong> vide l’écran
          d’accueil de l’app, qui affiche alors un message expliquant qu’on
          vérifie les offres.
        </p>
        <p>
          Ni l’un ni l’autre ne touche à l’argent&nbsp;: aucune réservation
          confirmée n’est annulée, aucun versement n’est interrompu, et les
          voyageurs déjà en séjour ne voient aucune différence. Suspendre est
          réversible en un clic.
        </p>
        <p>
          C’est pourquoi la règle est&nbsp;: <strong>en cas de doute, on
          suspend d’abord et on enquête ensuite.</strong> Un feed fermé deux
          heures coûte quelques réservations. Un feed qui affiche des offres
          fausses coûte la confiance, et celle-là ne revient pas. La page
          liste les cinq situations qui justifient une suspension et laquelle
          des deux appliquer pour chacune.
        </p>
      </Section>

      <Section n="11." title="Ce qui n’est pas automatisé">
        <p>Assumez que rien ne surveille ces points à votre place&nbsp;:</p>
        <ul>
          <li>
            <strong>L’âge des utilisateurs.</strong> Les CGU exigent 18 ans et
            l’utilisateur l’atteste à la connexion, mais rien ne le vérifie.
            Stripe contrôle l’identité des bénéficiaires de versements, pas
            celle des voyageurs.
          </li>
          <li>
            <strong>Les obligations fiscales et d’enregistrement</strong> des
            hôtes. Les CGU les renvoient à leur responsabilité&nbsp;; la
            plateforme ne collecte aucun numéro d’enregistrement.
          </li>
          <li>
            <strong>La déclaration à l’ARC</strong> des revenus des hôtes, si
            elle s’applique à la plateforme. À valider avec un comptable avant
            le lancement.
          </li>
          <li>
            <strong>Une offre réservée ne peut pas être retirée.</strong> Si un
            bénéficiaire se désiste après une réservation, cela se règle par un
            remboursement, pas par un retrait.
          </li>
        </ul>
      </Section>
    </div>
  );
}
